import { usePathname } from "./router";
import { useCallback, useState } from "react";
import { Star } from "./icons";
import { cn } from "./utils";
import { apiCall, useApi } from "./api/client";
import { favorites as favoritesContract } from "./api/contracts/favorites";

/** Strip the " · Excellent" suffix the shell appends to document titles. */
function cleanDocumentTitle(): string | null {
  if (typeof document === "undefined") return null;
  return document.title?.replace(/\s+·\s+Excellent\s*$/i, "").trim() || null;
}

/**
 * Shared favorites state + toggle for a given href (defaults to the current
 * pathname). Backs both `FavoriteToggle` (the standalone button) and the
 * favorite menu item rendered inside `PageNav`'s overflow menu, so the
 * favorites API logic lives in exactly one place.
 *
 * Pass `enabled = false` to skip the favorites list fetch entirely (SWR
 * receives a null key). Callers that render the hook unconditionally but
 * only sometimes surface a favorite control — e.g. PageNav's overflow menu,
 * which may carry only Reload/Settings — use this to avoid an idle fetch.
 */
export function useFavorite(hrefOverride?: string, enabled = true) {
  const pathname = usePathname();
  const href = hrefOverride ?? pathname;
  const { data } = useApi(favoritesContract.list, enabled ? {} : null);
  const [pending, setPending] = useState(false);
  const favorited = data?.favorites?.some((f) => f.href === href) ?? false;

  const toggle = useCallback(
    async (defaultLabel?: string) => {
      if (pending) return;
      setPending(true);
      const label = cleanDocumentTitle() || defaultLabel || href;
      try {
        // Cache invalidation is declared on the contract (RO-5-01,
        // `favorites.toggle.invalidates`) and runs automatically inside
        // `apiCall` — no manual `mutate(apiKey(...))` needed here anymore.
        // (The old manual call was a silent no-op under this shell's custom
        // SWR cache provider — see `client.ts`'s `registerBoundMutate`.)
        await apiCall(favoritesContract.toggle, { body: { href, label } });
      } finally {
        setPending(false);
      }
    },
    [pending, href],
  );

  return { href, favorited, pending, toggle };
}

interface FavoriteToggleProps {
  /** Fallback label when document.title is unset. */
  defaultLabel?: string;
  /**
   * "chip" (default) → bordered chip suitable for PageHeader toolbars.
   * "chrome" → flat 7×7 icon button styled like the rest of the top-nav
   *   right-rail (no border, hover bg-secondary/60).
   */
  variant?: "chip" | "chrome";
  /** Override pathname (rare — used when the page wants to favorite a
   * specific stable URL rather than whatever the router shows). */
  href?: string;
}

export function FavoriteToggle({
  defaultLabel,
  variant = "chip",
  href: hrefOverride,
}: FavoriteToggleProps) {
  const { favorited, pending, toggle } = useFavorite(hrefOverride);

  const chip =
    "inline-flex items-center justify-center rounded-md border border-border bg-background/40 p-1.5 transition-colors disabled:opacity-50";
  const chrome =
    "flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-50";

  return (
    <button
      type="button"
      onClick={() => toggle(defaultLabel)}
      aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
      aria-pressed={favorited}
      disabled={pending}
      title={favorited ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        variant === "chrome" ? chrome : chip,
        variant === "chrome"
          ? favorited
            ? "nav-active text-foreground"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          : favorited
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground hover:border-border/80",
      )}
    >
      <Star
        strokeWidth={1.75}
        className={cn("h-3.5 w-3.5", favorited && "fill-current")}
      />
    </button>
  );
}
